import { HttpStatusCode } from '@logz-pkg/enums';
import { agentsApiService } from './api.service';
import type { IAgentRunSessionResponse } from './general-agent.types';

export const POLL_INTERVAL_MS = 2000;
export const MAX_POLL_DURATION_MS = 60_000 * 10;

// Deliberately NOT React's MutableRefObject: this module is shared by every chat shell, and `packages/*` stays
// React-free. A React caller passes its refs straight through — the shapes are structurally identical.
export interface IPollAgentRunRefs {
  pollGenerationRef: { current: number };
  abortRef: { current: boolean };
  lastCompletedRunIdRef: { current: string | undefined };
}

export interface IPollAgentRunParams extends IPollAgentRunRefs {
  sessionId: string;
  generation: number;
  // Surfaces each non-done tick's run so the consumer can render the live thinking stream.
  onProgress?: (run: IAgentRunSessionResponse) => void;
}

export type PollAgentRunResult =
  // 'incomplete' = the stuck-run failsafe ended the run involuntarily (ORIONIQ-1182).
  | { status: 'success' | 'error' | 'stopped' | 'incomplete'; finalRun: IAgentRunSessionResponse }
  | { status: 'failed'; error?: unknown }
  | { status: 'cancelled' };

const delay = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

const getErrorStatus = (error: unknown): number | undefined => {
  const shape = error as { status?: number; response?: { status?: number } };

  return shape?.response?.status ?? shape?.status;
};

// Polls the general-agent run by sessionId until done. onProgress surfaces each NON-DONE tick's run so the
// consumer can render the live thinking stream (the terminal success/error result owns the done state, so
// the final blocks never render once without the answer). The lastCompletedRunId guard skips a previous
// turn's still-"done" run, so a multi-turn poll waits for the new run rather than re-surfacing the prior one.
export const pollAgentRunUntilDone = async ({
  sessionId,
  generation,
  pollGenerationRef,
  abortRef,
  lastCompletedRunIdRef,
  onProgress,
}: IPollAgentRunParams): Promise<PollAgentRunResult> => {
  const startedAt = Date.now();

  while (generation === pollGenerationRef.current && !abortRef.current) {
    if (Date.now() - startedAt >= MAX_POLL_DURATION_MS) return { status: 'failed' };

    let run: IAgentRunSessionResponse;

    try {
      run = await agentsApiService.getAgentRunBySessionId(sessionId);
    } catch (error) {
      if (getErrorStatus(error) === HttpStatusCode.NOT_FOUND) {
        await delay(POLL_INTERVAL_MS);
        continue;
      }

      return { status: 'failed', error };
    }

    // The seeded previous run — skip it whether it's done OR still running (a continue on a loaded conversation
    // whose newest run is still 'Running' must wait for the genuinely new run, not surface the in-flight one).
    const isPreviousRun = lastCompletedRunIdRef.current !== undefined && run.id === lastCompletedRunIdRef.current;

    if (isPreviousRun) {
      await delay(POLL_INTERVAL_MS);
      continue;
    }

    if (run.isDone) {
      if (run.id) lastCompletedRunIdRef.current = run.id;

      if (run.status === 'Error') return { status: 'error', finalRun: run };

      if (run.status === 'Stopped') return { status: 'stopped', finalRun: run };

      // The stuck-run failsafe ended the run involuntarily (ORIONIQ-1182) — a distinct terminal.
      if (run.status === 'Incomplete') return { status: 'incomplete', finalRun: run };

      return { status: 'success', finalRun: run };
    }

    if (generation === pollGenerationRef.current) onProgress?.(run);

    await delay(POLL_INTERVAL_MS);
  }

  return { status: 'cancelled' };
};
